const LOCAL_ID_PREFIXES = ['pending-', 'temp-'];

export const isLocalOnlyMessage = (msg) => {
  if (!msg) return false;
  if (msg.status === 'failed') return false;
  if (msg.status === 'pending' || msg.queued || msg.syncStatus === 'pending') {
    return true;
  }
  const id = String(msg.id || '');
  return LOCAL_ID_PREFIXES.some((prefix) => id.startsWith(prefix));
};

const toIdList = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((id) => String(id)).filter(Boolean);
};

/** Union of id lists — never shrinks (delivery/read receipts are sticky). */
export const unionIdLists = (...lists) => {
  const out = [];
  const seen = new Set();
  for (const list of lists) {
    for (const id of toIdList(list)) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};

const parseTruthyFlag = (value) => value === true || value === 'true';

const attachmentHasUrl = (att) => Boolean(att && (att.fileUrl || att.localFileId));

/** Keep unlocked file URLs when a later redacted payload only has names. */
export const preferAttachmentsWithUrls = (existingAtt, incomingAtt) => {
  const incoming = Array.isArray(incomingAtt) ? incomingAtt : null;
  const existing = Array.isArray(existingAtt) ? existingAtt : null;
  if (!incoming) return existing || [];
  if (!incoming.length) return existing?.length ? existing : incoming;
  if (incoming.some(attachmentHasUrl) || !existing?.some(attachmentHasUrl)) {
    return incoming;
  }
  const previous = new Map(
    existing.map((item) => [String(item.id || item.fileName || item.fileUrl || ''), item]),
  );
  return incoming.map((item) => {
    const prev = previous.get(String(item.id || item.fileName || item.fileUrl || ''));
    if (prev && attachmentHasUrl(prev) && !attachmentHasUrl(item)) {
      return { ...item, fileUrl: prev.fileUrl, localFileId: prev.localFileId };
    }
    return item;
  });
};

/**
 * Sticky merge for delivery/read fields so a stale create payload cannot
 * downgrade ✓✓ back to ✓.
 */
export const mergeStickyDeliveryFields = (existing = {}, incoming = {}) => {
  const readBy = unionIdLists(existing.readBy, incoming.readBy);
  const deliveredBy = unionIdLists(existing.deliveredBy, incoming.deliveredBy);
  const isRead =
    parseTruthyFlag(existing.isRead) ||
    parseTruthyFlag(incoming.isRead) ||
    parseTruthyFlag(incoming.read);
  const isDelivered =
    parseTruthyFlag(existing.isDelivered) ||
    parseTruthyFlag(incoming.isDelivered) ||
    parseTruthyFlag(incoming.delivered) ||
    isRead;

  return {
    readBy,
    deliveredBy,
    isRead,
    isDelivered,
  };
};

export const parseDeliveryFlagsFromPayload = (data = {}) => {
  const isReadValue =
    data.isRead !== undefined ? data.isRead : data.read;
  const isDeliveredValue =
    data.isDelivered !== undefined ? data.isDelivered : data.delivered;
  return {
    readBy: toIdList(data.readBy),
    deliveredBy: toIdList(data.deliveredBy),
    isRead: parseTruthyFlag(isReadValue),
    isDelivered:
      parseTruthyFlag(isDeliveredValue) || parseTruthyFlag(isReadValue),
  };
};

/** True when any non-sender user has confirmed delivery (or read). */
export const isMessageDeliveredToPeer = (msg) => {
  if (!msg) return false;
  if (parseTruthyFlag(msg.isDelivered) || parseTruthyFlag(msg.isRead)) {
    return true;
  }
  const senderId = msg.senderId != null ? String(msg.senderId) : null;
  const deliveredBy = toIdList(msg.deliveredBy);
  if (deliveredBy.some((id) => id !== senderId)) return true;
  const readBy = toIdList(msg.readBy);
  return readBy.some((id) => id !== senderId);
};

/** True when a participant other than the sender has opened/read the message. */
export const isMessageReadByPeer = (msg) => {
  if (!msg) return false;
  if (parseTruthyFlag(msg.isRead)) return true;
  const senderId = msg.senderId != null ? String(msg.senderId) : null;
  return toIdList(msg.readBy).some((id) => id !== senderId);
};

/**
 * DM ticks:
 * - none: saved only on this device (Realm / pending)
 * - single: accepted by the central server (left your machine)
 * - double: reached the other person's device (grey)
 * - read: the other person opened the chat (blue)
 */
export const getMessageTickState = (msg, { isDirectMessage, isOwn } = {}) => {
  if (!isDirectMessage || !isOwn || !msg || msg.status === 'failed') {
    return 'none';
  }
  if (isLocalOnlyMessage(msg)) {
    return 'none';
  }
  if (isMessageReadByPeer(msg)) {
    return 'read';
  }
  if (isMessageDeliveredToPeer(msg)) {
    return 'double';
  }
  return 'single';
};
